import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * "Connect Google for sign-in" for an ALREADY-SIGNED-IN account (a phone-authenticated owner or
 * operator). Unlike GoogleButton (which logs in via /auth/google/start), this hits the
 * authenticated /auth/google/link/start, which signs the current identity into the OAuth state so
 * the callback attaches Google to THIS account rather than resolving a fresh one.
 *
 * REMINDER: DO NOT HARDCODE THE URL OR ADD FALLBACKS — the return address is built from
 * window.location.origin, exactly as GoogleButton does.
 */
export const GoogleLinkButton = ({ testId = "google-link-btn" }) => {
  const { t, tenant } = useApp();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/auth/google/config")
      .then(({ data }) => setEnabled(!!data.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google/link/start", {
        return_url: `${window.location.origin}/auth/google`,
        tenant_id: tenant?.id,
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
      setLoading(false);
    }
  };

  if (!enabled) return null;

  return (
    <button
      type="button"
      data-testid={testId}
      disabled={loading}
      onClick={start}
      className="rounded-full border border-border bg-white px-5 py-2 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50 disabled:opacity-60"
    >
      {loading ? t("common.loading") : t("account.connectGoogle")}
    </button>
  );
};
